import { Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Fragment, createContext, useContext, useState, useCallback } from 'react';

import { cn } from '../utils/cn';

export interface Toast {
  id: string;
  title?: string;
  message: string;
  variant?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  action?: { label: string; onClick: () => void };
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export interface ToastProviderProps {
  children: React.ReactNode;
  /**
   * Default duration for toasts in milliseconds
   * @default 5000
   */
  defaultDuration?: number;
  /**
   * Maximum number of toasts to display
   * @default 5
   */
  maxToasts?: number;
}

/**
 * ToastProvider component
 *
 * Provider that manages toast notifications.
 * Should be placed at the root of your application.
 *
 * @example
 * ```tsx
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 * ```
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  defaultDuration = 5000,
  maxToasts = 5,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = Math.random().toString(36).substring(2, 9);
      const newToast: Toast = {
        ...toast,
        id,
        duration: toast.duration ?? defaultDuration,
      };

      setToasts((prev) => {
        const updated = [...prev, newToast];
        return updated.slice(-maxToasts);
      });

      if (newToast.duration && newToast.duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, newToast.duration);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- removeToast is stable (defined below with []) but referenced before declaration
    [defaultDuration, maxToasts]
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};

/**
 * Hook to use toast notifications
 *
 * @example
 * ```tsx
 * const { addToast } = useToast();
 *
 * addToast({
 *   title: 'Success',
 *   message: 'Your changes have been saved',
 *   variant: 'success',
 * });
 * ```
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useToast();

  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 z-[100] flex items-end px-4 py-6 sm:items-start sm:p-6"
    >
      <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
        ))}
      </div>
    </div>
  );
};

interface ToastItemProps {
  toast: Toast;
  onClose: () => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ toast, onClose }) => {
  const [show, setShow] = useState(true);

  const handleClose = () => {
    setShow(false);
    setTimeout(onClose, 300);
  };

  const variantStyles = {
    success: {
      container: 'bg-success-50 border-success-200',
      icon: 'text-success-400',
      text: 'text-success-800',
      button: 'text-success-500 hover:text-success-600',
    },
    error: {
      container: 'bg-error-50 border-error-200',
      icon: 'text-error-400',
      text: 'text-error-800',
      button: 'text-error-500 hover:text-error-600',
    },
    warning: {
      container: 'bg-warning-50 border-warning-200',
      icon: 'text-warning-400',
      text: 'text-warning-800',
      button: 'text-warning-500 hover:text-warning-600',
    },
    info: {
      container: 'bg-info-50 border-info-200',
      icon: 'text-info-400',
      text: 'text-info-800',
      button: 'text-info-500 hover:text-info-600',
    },
  };

  const icons = {
    success: CheckCircleIcon,
    error: XCircleIcon,
    warning: ExclamationTriangleIcon,
    info: InformationCircleIcon,
  };

  const variant = toast.variant || 'info';
  const Icon = icons[variant];
  const styles = variantStyles[variant];

  return (
    <Transition
      show={show}
      as={Fragment}
      enter="transform ease-out duration-300 transition"
      enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enterTo="translate-y-0 opacity-100 sm:translate-x-0"
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg border shadow-lg">
        <div className={cn('p-4', styles.container)}>
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <Icon className={cn('h-6 w-6', styles.icon)} aria-hidden="true" />
            </div>
            <div className="ml-3 w-0 flex-1 pt-0.5">
              {toast.title && (
                <p className={cn('text-sm font-semibold', styles.text)}>{toast.title}</p>
              )}
              <p className={cn('text-sm', toast.title && 'mt-1', styles.text)}>{toast.message}</p>
              {toast.action && (
                <button
                  type="button"
                  className={cn('mt-2 text-sm font-medium underline', styles.button)}
                  onClick={() => {
                    toast.action!.onClick();
                    handleClose();
                  }}
                >
                  {toast.action.label}
                </button>
              )}
            </div>
            <div className="ml-4 flex flex-shrink-0">
              <button
                type="button"
                className={cn(
                  'inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2',
                  styles.button
                )}
                onClick={handleClose}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};
